import { Filter, SAndPRating } from '../filter/types'
import { OrderType } from '../order/types'
import { WatchList } from '../watchList/types'
import { PageUpdate, Security } from './reducer'
import { QuoteReliability } from './types'

export const fetchSecuritiesByIds = (ids: Array<Security['id']>) =>
  ({ type: 'securities.fetchSecuritiesByIds', payload: ids } as const)
export type FetchSecuritiesByIdsAction = ReturnType<typeof fetchSecuritiesByIds>

export const addOrUpdateSecurities = (
  gridIndex: number,
  securities: Security[],
  page: number
) =>
  ({
    type: 'securities.addOrUpdateSecurities',
    payload: { gridIndex, securities, page }
  } as const)
export type AddOrUpdateSecuritiesAction = ReturnType<
  typeof addOrUpdateSecurities
>

export const sortValidationsToTopAction = (
  gridIndex: number,
  sortToTop: number[]
) =>
  ({
    type: 'securities.sortValidationsToTopAction',
    payload: { gridIndex, sortToTop }
  } as const)
export type SortValidationsToTopAction = ReturnType<
  typeof sortValidationsToTopAction
>

export const insertSecurity = (
  gridIndex: number,
  securityId: number,
  insertBefore: number
) =>
  ({
    type: 'securities.insertSecurity',
    payload: { gridIndex, securityId, nextSecurityId: insertBefore }
  } as const)
export type InsertSecurityAction = ReturnType<typeof insertSecurity>

export const processInsertSecurity = (gridIndex: number, update: PageUpdate) =>
  ({
    type: 'securities.processInsertSecurity',
    payload: { gridIndex, update }
  } as const)
export type ProcessInsertSecurityAction = ReturnType<
  typeof processInsertSecurity
>

export const resetSortToTop = (gridIndex: number) =>
  ({ type: 'securities.resetSortToTop', payload: { gridIndex } } as const)
export type ResetSortToTopAction = ReturnType<typeof resetSortToTop>

export const addOrUpdateSecuritiesCache = (securities: Security[]) =>
  ({
    type: 'securities.addOrUpdateSecuritiesCache',
    payload: { securities }
  } as const)
export type AddOrUpdateSecuritiesCacheAction = ReturnType<
  typeof addOrUpdateSecuritiesCache
>

export const saveAdvancedFilter = (gridIndex: number, filter: Filter) =>
  ({
    type: 'securities.saveAdvancedFilter',
    payload: { gridIndex, filter }
  } as const)
export type SaveAdvancedFilterAction = ReturnType<typeof saveAdvancedFilter>

export const setWatchListId = (
  gridIndex: number,
  watchlistId: number | undefined
) =>
  ({
    type: 'securities.setWatchListId',
    payload: { gridIndex, watchlistId }
  } as const)
export type SetWatchListId = ReturnType<typeof setWatchListId>

export const setSecuritiesFilter = (
  gridIndex: number,
  securityIds: Array<Security['id']> | undefined
) =>
  ({
    type: 'securities.setSecuritiesFilter',
    payload: { gridIndex, securityIds }
  } as const)
export type SetSecuritiesFilterAction = ReturnType<typeof setSecuritiesFilter>

export const setIssuerFilter = (
  gridIndex: number,
  issuerFilter: string | undefined
) =>
  ({
    type: 'securities.setIssuerFilter',
    payload: { gridIndex, issuerFilter }
  } as const)
export type SetIssuerFilterAction = ReturnType<typeof setIssuerFilter>

export const setIsMine = (gridIndex: number, isMine: boolean) =>
  ({ type: 'securities.setIsMine', payload: { gridIndex, isMine } } as const)
export type SetIsMine = ReturnType<typeof setIsMine>

export const setShowRfqEligible = (gridIndex: number, isRFQEligible: boolean) =>
  ({
    type: 'securities.setShowRfqEligible',
    payload: { gridIndex, isRFQEligible }
  } as const)
export type SetShowRfqEligibleAction = ReturnType<typeof setShowRfqEligible>

export const setRfqEntryVisible = (
  gridIndex: number,
  securityId: number,
  type: OrderType,
  isVisible: boolean
) =>
  ({
    type: 'securities.setRfqEntryVisible',
    payload: { gridIndex, securityId, type, isVisible }
  } as const)
export type SetRfqEntryVisibleAction = ReturnType<typeof setRfqEntryVisible>

export const setRfqEntryOrderId = (
  gridIndex: number,
  securityId: number,
  type: OrderType,
  orderId: string
) =>
  ({
    type: 'securities.setRfqEntryOrderId',
    payload: { gridIndex, securityId, orderId, type }
  } as const)
export type SetRfqEntryOrderIdAction = ReturnType<typeof setRfqEntryOrderId>

export const unsubscribeGetSecurities = () =>
  ({ type: 'securities.unsubscribeGetSecurities', payload: null } as const)
export type UnsubscribeGetSecurities = ReturnType<
  typeof unsubscribeGetSecurities
>

export const setShowLive = (gridIndex: number, showLive: boolean) =>
  ({
    type: 'securities.setShowLive',
    payload: { gridIndex, showLive }
  } as const)
export type SetShowLive = ReturnType<typeof setShowLive>

export const setQueryFilter = (gridIndex: number, query?: string) =>
  ({
    type: 'securities.setQueryFilter',
    payload: { gridIndex, query }
  } as const)
export type SetQueryFilterAction = ReturnType<typeof setQueryFilter>

export const setQuoteReliability = (
  gridIndex: number,
  quoteReliability: QuoteReliability
) =>
  ({
    type: 'securities.setQuoteReliability',
    payload: { gridIndex, quoteReliability }
  } as const)
export type SetQuoteReliability = ReturnType<typeof setQuoteReliability>

export const applyQuickFilters = (
  gridIndex: number,
  myFirm: boolean,
  myBook: boolean,
  size: number
) =>
  ({
    type: 'securities.applyQuickFilters',
    payload: { gridIndex, myFirm, myBook, size }
  } as const)
export type ApplyQuickFiltersAction = ReturnType<typeof applyQuickFilters>

export const setUseAdvancedFilter = (
  gridIndex: number,
  useAdvancedFilter: boolean
) =>
  ({
    type: 'securities.setUseAdvancedFilter',
    payload: { gridIndex, useAdvancedFilter }
  } as const)
export type SetUseAdvancedFilter = ReturnType<typeof setUseAdvancedFilter>

export const createGrid = (
  watchlistId: WatchList['id'],
  afterIndex: number,
  isMineGrid: boolean
) =>
  ({
    type: 'securities.createGrid',
    payload: { watchlistId, afterIndex, isMineGrid }
  } as const)
export type CreateGridAction = ReturnType<typeof createGrid>

export const removeGrid = (gridIndex: number) =>
  ({ type: 'securities.removeGrid', payload: { gridIndex } } as const)
export type RemoveGridAction = ReturnType<typeof removeGrid>

export const fetchListTradingSecurities = (watchlistId: number, page: number) =>
  ({
    type: 'securities.fetchListTradingSecurities',
    payload: { watchlistId, page }
  } as const)
export type FetchListTradingSecuritiesAction = ReturnType<
  typeof fetchListTradingSecurities
>

export const addOrUpdateListTradingSecurities = (
  securities: Security[],
  page: number
) =>
  ({
    type: 'securities.addOrUpdateListTradingSecurities',
    payload: { securities, page }
  } as const)
export type AddOrUpdateListTradingSecuritiesAction = ReturnType<
  typeof addOrUpdateListTradingSecurities
>

export const fetchListTradingSecuritiesFailure = () =>
  ({ type: 'securities.fetchListTradingSecuritiesFailure' } as const)
export type FetchListTradingSecuritiesFailureAction = ReturnType<
  typeof fetchListTradingSecuritiesFailure
>

export const unsubscribeListTrading = () =>
  ({ type: 'securities.unsubscribeListTrading' } as const)
export type UnsubscribeListTradingAction = ReturnType<
  typeof unsubscribeListTrading
>

export type ListTradingActions =
  | FetchListTradingSecuritiesAction
  | AddOrUpdateListTradingSecuritiesAction
  | FetchListTradingSecuritiesFailureAction
  | UnsubscribeListTradingAction

export const showSecurityDetails = (
  gridIndex: number,
  securityId: Security['id'] | undefined
) =>
  ({
    type: 'securities.showSecurityDetails',
    payload: { gridIndex, securityId }
  } as const)
export type ShowSecurityDetailsAction = ReturnType<typeof showSecurityDetails>

export const getAllSecurities = () =>
  ({ type: 'securities.getAllSecurities' } as const)
export type GetAllSecuritiesAction = ReturnType<typeof getAllSecurities>

export const getAllSecuritiesSucceeded = () =>
  ({ type: 'securities.getAllSecuritiesSucceeded' } as const)
export type GetAllSecuritiesSucceededAction = ReturnType<
  typeof getAllSecuritiesSucceeded
>

export const patchTickerRatings = (ticker: string, sAndPRating: SAndPRating) =>
  ({
    type: 'securities.patchTickerRatings',
    payload: { ticker, sAndPRating }
  } as const)
export type PatchTickerRatingsAction = ReturnType<typeof patchTickerRatings>

export const getAllSecuritiesFailed = () =>
  ({ type: 'securities.getAllSecuritiesFailed' } as const)
export type GetAllSecuritiesFailedAction = ReturnType<
  typeof getAllSecuritiesFailed
>

export const fetchExportSecurities = (
  gridIndex: number,
  includeMakerName: boolean
) =>
  ({
    type: 'securities.fetchExportSecurities',
    payload: { gridIndex, includeMakerName }
  } as const)
export type FetchExportSecuritiesAction = ReturnType<
  typeof fetchExportSecurities
>

export const fetchExportSecuritiesSucceeded = (securities: Security[]) =>
  ({
    type: 'securities.fetchExportSecuritiesSucceeded',
    payload: { securities }
  } as const)
export type FetchExportSecuritiesSucceededAction = ReturnType<
  typeof fetchExportSecuritiesSucceeded
>

export const fetchExportSecuritiesFailed = () =>
  ({ type: 'securities.fetchExportSecuritiesFailed' } as const)
export type FetchExportSecuritiesFailedAction = ReturnType<
  typeof fetchExportSecuritiesFailed
>

export const fetchGridViewState = (gridIndex: number) =>
  ({ type: 'securities.fetchGridViewState', payload: gridIndex } as const)
export type FetchGridViewStateAction = ReturnType<typeof fetchGridViewState>

export const fetchGridViewStateSucceeded = (
  gridIndex: number,
  securityIds: number[]
) =>
  ({
    type: 'securities.fetchGridViewStateSucceeded',
    payload: { gridIndex, securityIds }
  } as const)
export type FetchGridViewStateSucceededAction = ReturnType<
  typeof fetchGridViewStateSucceeded
>

export const fetchGridViewStateFailed = (gridIndex: number, error: unknown) =>
  ({
    type: 'securities.fetchGridViewStateFailed',
    payload: { error, gridIndex }
  } as const)
export type FetchGridViewStateFailedAction = ReturnType<
  typeof fetchGridViewStateFailed
>

let callId = 0
export const subscribeGridViewport = (
  gridIndex: number,
  securityIds: number[]
) =>
  ({
    type: 'securities.subscribeGridViewport',
    payload: { gridIndex, securityIds, callId: ++callId }
  } as const)
export type SubscribeGridViewportAction = ReturnType<
  typeof subscribeGridViewport
>

export type ResetSortToTopTriggerActions =
  | SetWatchListId
  | SetSecuritiesFilterAction
  | SetIssuerFilterAction
  | SetIsMine

export type GridSpecificAction =
  | AddOrUpdateSecuritiesAction
  | InsertSecurityAction
  | ProcessInsertSecurityAction
  | SetQueryFilterAction
  | SetUseAdvancedFilter
  | SetShowLive
  | SetShowRfqEligibleAction
  | SetRfqEntryVisibleAction
  | SetRfqEntryOrderIdAction
  | SetQuoteReliability
  | SortValidationsToTopAction
  | ResetSortToTopAction
  | ResetSortToTopTriggerActions
  | SaveAdvancedFilterAction
  | ApplyQuickFiltersAction
  | UnsubscribeGetSecurities
  | ShowSecurityDetailsAction

export type SecuritiesAction =
  | GridSpecificAction
  | AddOrUpdateSecuritiesCacheAction
  | CreateGridAction
  | RemoveGridAction
  | UnsubscribeGetSecurities
  | ListTradingActions
  | PatchTickerRatingsAction
  | FetchExportSecuritiesAction
  | FetchExportSecuritiesSucceededAction
  | FetchExportSecuritiesFailedAction
  | GetAllSecuritiesAction
  | GetAllSecuritiesSucceededAction
  | GetAllSecuritiesFailedAction

export type GridViewStateAction =
  | FetchGridViewStateAction
  | FetchGridViewStateSucceededAction
  | FetchGridViewStateFailedAction
  | ApplyQuickFiltersAction
  | SetRfqEntryVisibleAction
  | SetRfqEntryOrderIdAction
